// Central API base URLs.
//
// When CORE-THEME runs standalone (npm run dev), the values come from
// src/services/.env (Vite VITE_* vars, baked in at build time).
//
// When CORE-THEME is consumed as the published @realtek/core-theme package,
// host apps can override these values at RUNTIME by setting
// `window.__CORE_THEME_CONFIG__` before the app renders:
//
//   <script>
//     window.__CORE_THEME_CONFIG__ = {
//       VITE_AUTH_URL:        'http://192.168.1.66/authapi/v1',
//       VITE_SUBMISSIONS_URL: 'http://192.168.1.66/submissionsapi/v1',
//       VITE_JOBS_URL:        'http://192.168.1.66/jobsapi/v1',
//       VITE_CANDIDATES_URL:  'http://192.168.1.66/candidatesapi/v1',
//       VITE_APP_ID:          'zinnext',   // sent as the `X-App-Id` header on every API call
//       VITE_ADMIN_APP_ID:    'zinnext',   // optional admin override
//     };
//   </script>
//
// Priority: runtime window config  >  build-time .env fallback.

const BUILD_TIME_FALLBACK = {
  VITE_AUTH_URL: import.meta.env.VITE_AUTH_URL,
  VITE_ADMIN_API_URL: import.meta.env.VITE_ADMIN_API_URL,
  VITE_ADMIN_APP_ID: import.meta.env.VITE_ADMIN_APP_ID,
  VITE_SUBMISSIONS_URL: import.meta.env.VITE_SUBMISSIONS_URL,
  VITE_JOBS_URL: import.meta.env.VITE_JOBS_URL,
  VITE_CANDIDATES_URL: import.meta.env.VITE_CANDIDATES_URL,
  VITE_PYOPS_URL: import.meta.env.VITE_PYOPS_URL,
  VITE_APP_ID: import.meta.env.VITE_APP_ID,
};

function resolve(key) {
  if (
    typeof window !== 'undefined' &&
    window.__CORE_THEME_CONFIG__ &&
    window.__CORE_THEME_CONFIG__[key] != null
  ) {
    return window.__CORE_THEME_CONFIG__[key];
  }
  return BUILD_TIME_FALLBACK[key];
}

function runtimeValue(key, fallback = '') {
  return {
    toString: () => resolve(key) || (typeof fallback === 'function' ? fallback() : fallback),
    valueOf: () => resolve(key) || (typeof fallback === 'function' ? fallback() : fallback),
    [Symbol.toPrimitive]: () => resolve(key) || (typeof fallback === 'function' ? fallback() : fallback),
  };
}

// These are intentionally dynamic. Many service files use them inside template
// literals (`${AUTH_URL}/path`) or pass them to helpers that later build a URL.
// Resolving at string-conversion time prevents a published package from
// freezing CORE-THEME's own .env values into consuming apps and Docker builds.
export const AUTH_URL = runtimeValue('VITE_AUTH_URL');
export const ADMIN_API_URL = runtimeValue(
  'VITE_ADMIN_API_URL',
  () => resolve('VITE_AUTH_URL') || 'http://localhost:9009',
);
export const ADMIN_APP_ID = runtimeValue('VITE_ADMIN_APP_ID', () => resolve('VITE_APP_ID') || 'zinnext-dev-api-v1');
export const SUBMISSIONS_URL = runtimeValue('VITE_SUBMISSIONS_URL');
export const JOBS_URL = runtimeValue('VITE_JOBS_URL');
export const CANDIDATES_URL = runtimeValue('VITE_CANDIDATES_URL');
export const PYOPS_URL = runtimeValue('VITE_PYOPS_URL', 'http://192.168.1.66/pyOps/v1');

// App identifier sent as the `X-App-Id` header on every API request (e.g.
// 'zinnext', 'lms'). Configured in ONE place — the host's window config (or the
// build-time .env fallback) — and applied to all calls via the interceptor below.
export const APP_ID = runtimeValue('VITE_APP_ID');

// Helper for code that builds headers explicitly: merges `X-App-Id` into a
// header object without clobbering anything else. (The interceptor below already
// covers existing calls; this is just a convenience for new code.)
export function withAppId(headers = {}) {
  const appId = resolve('VITE_APP_ID');
  return appId ? { 'X-App-Id': appId, ...headers } : { ...headers };
}

export function withAdminAppId(headers = {}) {
  const appId = resolve('VITE_ADMIN_APP_ID') || resolve('VITE_APP_ID') || 'zinnext-dev-api-v1';
  return appId ? { 'X-App-Id': appId, ...headers } : { ...headers };
}

// ── X-App-Id interceptor ──────────────────────────────────────────────────────
//
// Rather than editing every inline `headers: {...}` across the service files
// (and risking new calls forgetting it), we patch `fetch` ONCE so every request
// to a configured API base URL carries `X-App-Id`. This is scoped strictly to the
// four API origins, so third-party/asset requests are never touched, and any
// caller that already set `X-App-Id` explicitly is left as-is.
function installAppIdInterceptor() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  // Guard against double-patching (repeated imports / Vite HMR).
  if (window.__CORE_THEME_APP_ID_PATCHED__) return;

  const apiBaseUrls = [AUTH_URL, ADMIN_API_URL, SUBMISSIONS_URL, JOBS_URL, CANDIDATES_URL, PYOPS_URL];
  const originalFetch = window.fetch.bind(window);

  const urlOf = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url; // Request object
    return '';
  };
  const isApiRequest = (url) =>
    !!url && apiBaseUrls.some((base) => {
      const value = String(base);
      return value && url.startsWith(value);
    });

  window.fetch = function patchedFetch(input, init) {
    try {
      const url = urlOf(input);
      const isAdminRequest = String(ADMIN_API_URL) && url.startsWith(String(ADMIN_API_URL));
      const appId = isAdminRequest
        ? (resolve('VITE_ADMIN_APP_ID') || resolve('VITE_APP_ID') || 'zinnext-dev-api-v1')
        : resolve('VITE_APP_ID');
      if (appId && isApiRequest(url)) {
        const isRequestObj = typeof Request !== 'undefined' && input instanceof Request;
        const headers = new Headers(
          (init && init.headers) || (isRequestObj ? input.headers : undefined),
        );
        if (!headers.has('X-App-Id')) headers.set('X-App-Id', appId);
        if (isRequestObj && !(init && init.headers)) {
          return originalFetch(new Request(input, { headers }), init);
        }
        return originalFetch(input, { ...(init || {}), headers });
      }
    } catch {
      // Any unexpected argument shape: fall through to the untouched request.
    }
    return originalFetch(input, init);
  };

  window.__CORE_THEME_APP_ID_PATCHED__ = true;
}

installAppIdInterceptor();

// Optional JS helper (alternative to the index.html <script>). Because the
// exports above are resolved when this module first loads, call this BEFORE the
// first import of @realtek/core-theme — e.g. in the host's index.html, not in
// main.jsx (which runs after imports are evaluated). The index.html <script>
// approach is the reliable one.
export function configureCoreTheme(config = {}) {
  if (typeof window === 'undefined') return;
  window.__CORE_THEME_CONFIG__ = { ...(window.__CORE_THEME_CONFIG__ || {}), ...config };
  installAppIdInterceptor();
}
